<template>
  <transition name="auth-fade" appear>
    <div v-if="visible" class="auth-modal-overlay">
      <div class="auth-modal-dark">
        <button class="auth-close" @click="$emit('close')">
          <svg class="auth-close-icon" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
            <circle cx="20" cy="20" r="18" stroke="white" stroke-width="1.8" fill="transparent" />
            <line x1="14" y1="14" x2="26" y2="26" stroke="white" stroke-width="1.8" />
            <line x1="26" y1="14" x2="14" y2="26" stroke="white" stroke-width="1.8" />
          </svg>
        </button>
        <div class="auth-header">{{ mode === 'login' ? 'log in to save tracks and access your library' : 'sign up to save tracks and access your library' }}</div>
        <button class="google-btn-dark" @click="googleSignIn">
          <img v-if="googleLogoLoaded" src="/playground_assets/google.png" alt="Google logo" class="google-logo-dark" @error="googleLogoLoaded = false" />
          <svg v-else class="google-logo-dark" viewBox="0 0 48 48"><g><path fill="#4285F4" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.23l6.87-6.87C36.68 2.36 30.77 0 24 0 14.82 0 6.71 5.06 2.69 12.44l8.06 6.26C12.36 13.13 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.1 24.55c0-1.64-.15-3.22-.43-4.74H24v9.01h12.42c-.54 2.9-2.18 5.36-4.65 7.01l7.19 5.6C43.98 37.13 46.1 31.3 46.1 24.55z"/><path fill="#FBBC05" d="M10.75 28.7c-1.01-2.99-1.01-6.21 0-9.2l-8.06-6.26C.64 16.13 0 19.01 0 22c0 2.99.64 5.87 1.81 8.76l8.94-7.06z"/><path fill="#EA4335" d="M24 44c6.48 0 11.92-2.14 15.89-5.82l-7.19-5.6c-2.01 1.35-4.59 2.15-8.7 2.15-6.26 0-11.64-3.63-13.25-8.7l-8.94 7.06C6.71 42.94 14.82 48 24 48z"/></g></svg>
        </button>
        <div class="auth-divider-dark">or</div>
        <form class="auth-form-dark" @submit.prevent="mode === 'login' ? login() : register()">
          <input v-if="mode === 'register'" v-model="username" type="text" placeholder="username" required />
          <input v-model="email" type="email" placeholder="email" required />
          <input v-model="password" type="password" placeholder="password" required />
          <button type="submit" class="main-btn-dark">
            {{ mode === 'login' ? 'log in' : 'sign up' }}
          </button>
      </form>
        <div v-if="mode === 'register'" class="auth-newsletter-dark">
          <label class="newsletter-checkbox">
            <input type="checkbox" v-model="newsletterOptIn" />
            <span class="checkmark"></span>
            stay updated with our newsletters
          </label>
        </div>
        <div class="auth-switch-dark">
          <span v-if="mode === 'login'">no account? <a href="#" @click.prevent="mode = 'register'">sign up</a></span>
          <span v-else>already signed up? <a href="#" @click.prevent="mode = 'login'">log in</a></span>
        </div>
        <div v-if="error" class="auth-error-dark">{{ error }}</div>
        <div v-if="mode === 'login'" class="auth-forgot-dark"><a href="#" @click.prevent="showForgot = true">forgot password?</a></div>
        <div class="auth-terms-dark"><a href="#" @click.prevent="showTerms = true">Terms & Conditions</a></div>
        
        <!-- Debug button (remove in production) -->
        <!-- <button @click="testFirebaseConnection" style="margin-top: 10px; padding: 5px 10px; background: #333; color: white; border: none; font-size: 12px;">
          Test Firebase Connection
        </button> -->
      </div>
      <div v-show="showTerms" class="terms-modal-overlay">
        <div class="terms-modal-dark">
          <button class="terms-close" @click="showTerms = false">×</button>
          <div class="terms-title">Terms & Conditions</div>
          <div class="terms-content terms-content-small">
            <p>By creating an account, you agree to the storage of your username, email, and saved tracks for the purpose of providing your Nuance Radio library. You may delete your account and all associated data at any time from your profile. Your data will never be sold or shared with third parties. For more information, contact us at contact@nuance.radio.</p>
          </div>
        </div>
      </div>
      <div v-show="showForgot" class="terms-modal-overlay">
        <div class="terms-modal-dark">
          <button class="terms-close" @click="showForgot = false">×</button>
          <div class="terms-title">Reset Password</div>
          <div class="terms-content terms-content-small">
            <form @submit.prevent="sendReset">
              <input v-model="resetEmail" type="email" placeholder="your email" required style="width:100%;margin-bottom:1rem;padding:0.7rem;border-radius:0;border:none;text-align:center;" />
              <button type="submit" class="main-btn-dark" style="margin-bottom:0.5rem;">Send reset link</button>
            </form>
            <div v-if="resetMsg" :style="{color: resetMsgType === 'success' ? '#4caf50' : '#ff7676', 'text-align': 'center', 'margin-top': '0.5rem', 'font-size': '0.9rem'}">{{ resetMsg }}</div>
          </div>
    </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

export default {
  name: 'Auth',
  props: {
    mode: {
      type: String,
      default: 'login'
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      email: '',
      password: '',
      username: '',
      user: null,
      error: '',
      showTerms: false,
      showForgot: false,
      resetEmail: '',
      resetMsg: '',
      resetMsgType: '',
      googleLogoLoaded: true,
      newsletterOptIn: false
    };
  },
  created() {
    onAuthStateChanged(auth, user => {
      this.user = user;
    });
  },
  methods: {
    async login() {
      this.error = '';
      try {
        await signInWithEmailAndPassword(auth, this.email, this.password);
        this.$emit('close');
      } catch (e) {
        this.error = e.message;
      }
    },
    async register() {
      this.error = '';
      try {
        console.log('Attempting to register user:', this.email);
        const result = await createUserWithEmailAndPassword(auth, this.email, this.password);
        console.log('User created successfully:', result.user.uid);
        
        // Update profile with username
        await updateProfile(result.user, { displayName: this.username });
        console.log('Profile updated with username:', this.username);
        
        // Save newsletter preference
        await setDoc(doc(db, 'users', result.user.uid), {
          newsletterOptIn: this.newsletterOptIn
        });
        console.log('Newsletter preference saved for user:', result.user.uid);
        
        // Send email verification manually
        if (result.user && !result.user.emailVerified) {
          console.log('Sending email verification...');
        await sendEmailVerification(result.user);
          console.log('Email verification sent successfully');
        }
        
        this.mode = 'login';
        this.error = 'Registration successful! Please check your email for a verification link. (check your spams)';
      } catch (e) {
        console.error('Registration error:', e.code, e.message);
        this.error = this.getErrorMessage(e.code);
      }
    },
    async googleSignIn() {
      this.error = '';
      try {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        
        // Check if this is a new user (first time signing in)
        if (result._tokenResponse?.isNewUser) {
          // For new Google users, save newsletter preference (default to false)
          await setDoc(doc(db, 'users', result.user.uid), {
            newsletterOptIn: false
          });
          console.log('New Google user created, newsletter preference saved');
        }
        
        this.$emit('close');
      } catch (e) {
        this.error = e.message;
      }
    },
    async sendReset() {
      this.resetMsg = '';
      this.resetMsgType = '';
      try {
        await sendPasswordResetEmail(auth, this.resetEmail);
        this.resetMsg = 'Reset link sent! Check your email. (check your spams)';
        this.resetMsgType = 'success';
      } catch (e) {
        this.resetMsg = this.getErrorMessage(e.code);
        this.resetMsgType = 'error';
      }
    },
    getErrorMessage(errorCode) {
      const errorMessages = {
        'auth/email-already-in-use': 'This email is already registered. Try logging in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password should be at least 6 characters long.',
        'auth/user-not-found': 'No account found with this email address.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
        'auth/operation-not-allowed': 'Email/password authentication is not enabled. Please contact support.',
        'auth/user-disabled': 'This account has been disabled. Please contact support.',
        'auth/invalid-credential': 'Invalid email or password.',
        'auth/requires-recent-login': 'Please log out and log back in to continue.',
        'auth/email-change-needs-verification': 'Please verify your email before changing it.',
        'auth/credential-already-in-use': 'This account is already linked to another sign-in method.',
        'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
        'auth/invalid-verification-code': 'Invalid verification code.',
        'auth/invalid-verification-id': 'Invalid verification ID.',
        'auth/missing-verification-code': 'Verification code is required.',
        'auth/missing-verification-id': 'Verification ID is required.',
        'auth/quota-exceeded': 'Service temporarily unavailable. Please try again later.',
        'auth/operation-not-supported-in-this-environment': 'This operation is not supported in this environment.',
        'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations.',
        'auth/invalid-argument': 'Invalid argument provided.',
        'auth/invalid-provider-id': 'Invalid provider ID.',
        'auth/invalid-oauth-provider': 'Invalid OAuth provider.',
        'auth/invalid-oauth-client-id': 'Invalid OAuth client ID.',
        'auth/invalid-oauth-client-secret': 'Invalid OAuth client secret.',
        'auth/invalid-oauth-redirect-uri': 'Invalid OAuth redirect URI.',
        'auth/invalid-oauth-scope': 'Invalid OAuth scope.',
        'auth/invalid-oauth-state': 'Invalid OAuth state.',
        'auth/invalid-oauth-code': 'Invalid OAuth code.',
        'auth/invalid-oauth-token': 'Invalid OAuth token.',
        'auth/invalid-oauth-refresh-token': 'Invalid OAuth refresh token.',
        'auth/invalid-oauth-access-token': 'Invalid OAuth access token.',
        'auth/invalid-oauth-id-token': 'Invalid OAuth ID token.',
        'auth/invalid-oauth-custom-token': 'Invalid OAuth custom token.',
        'auth/invalid-oauth-credential': 'Invalid OAuth credential.',
        'auth/invalid-oauth-provider-data': 'Invalid OAuth provider data.',
        'auth/invalid-tenant-id': 'Invalid tenant ID.',
        'auth/tenant-id-mismatch': 'Tenant ID mismatch.',
        'auth/invalid-persistence-type': 'Invalid persistence type.',
        'auth/unsupported-persistence-type': 'This persistence type is not supported.'
      };
      
      return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
    }
  }
};
</script>

<style scoped>
.auth-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 20000;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
}

/* When the modal is in its final state, make background opaque */
.auth-modal-overlay.auth-fade-enter-to,
.auth-modal-overlay.auth-fade-leave {
  background: rgba(0,0,0,0.6) !important;
}
.auth-modal-dark {
  position: relative;
  background: #000;
  border-radius: 0;
  box-shadow: none;
  padding: 1.2rem 1.2rem 0.7rem 1.2rem;
  min-width: 0;
  max-width: 340px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}
.auth-close {
  position: absolute;
  top: -20px;
  right: -20px;
  background: transparent;
  border: none;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20001;
}

.auth-close-icon {
  width: 32px;
  height: 32px;
  display: block;
}
.auth-header {
  color: #fff;
  font-family: Syne, sans-serif;
  font-size: 1rem;
  font-weight: 300;
  text-align: center;
  margin-bottom: 1.1rem;
  margin-top: 0.2rem;
  letter-spacing: 0.01em;
}
.google-btn-dark {
  display: flex;
  align-items: center;
  background: #222;
  border: none;
  border-radius: 0;
  padding: 0.7rem 0;
  width: 100%;
  justify-content: center;
  margin-bottom: 1.1rem;
  margin-top: 0.1rem;
  cursor: pointer;
  transition: background 0.2s;
}
.google-btn-dark:hover {
  background: #333;
}
.google-logo-dark {
  width: 28px;
  height: 28px;
  display: block;
  margin: 0 auto;
}
.auth-divider-dark {
  color: #fff;
  text-align: center;
  margin: 0.2rem 0 1.1rem 0;
  width: 100%;
  font-size: 1rem;
  font-family: Syne, sans-serif;
  font-weight: 400;
}
.auth-form-dark {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.auth-form-dark input {
  width: 100%;
  margin-bottom: 0.9rem;
  padding: 0.8rem 1rem;
  border-radius: 0;
  border: none;
  background: #fff;
  color: #000;
  font-size: 1rem;
  font-family: Syne, sans-serif;
  font-weight: 400;
  letter-spacing: 0.01em;
  text-transform: lowercase;
  text-align: center;
}
.auth-form-dark input::placeholder {
  color: #888;
  text-transform: lowercase;
  text-align: center;
}
.main-btn-dark {
  width: 100%;
  padding: 0.8rem 0;
  border-radius: 0;
  border: none;
  background: #888;
  color: #fff;
  font-size: 1.1rem;
  font-family: Syne, sans-serif;
  font-weight: 600;
  margin-bottom: 0.5rem;
  cursor: pointer;
  text-transform: lowercase;
}
.auth-switch-dark {
  margin-top: 0.7rem;
  font-size: 1rem;
  color: #fff;
  text-align: center;
  font-family: Syne, sans-serif;
}
.auth-switch-dark a {
  color: #fff;
  text-decoration: underline;
  cursor: pointer;
}
.auth-error-dark {
  color: #ff7676;
  margin-top: 1rem;
  text-align: center;
  font-size: 1rem;
}
.auth-terms-dark {
  color: #fff;
  font-size: 0.85rem;
  margin-top: 1.1rem;
  margin-bottom: 0.2rem;
  text-align: center;
  font-family: Syne, sans-serif;
  opacity: 0.7;
}
.auth-terms-dark a {
  color: #fff;
  font-size: 0.6rem;
  text-decoration: underline;
  cursor: pointer;
}
.auth-forgot-dark {
  color: #fff;
  font-size: 0.85rem;
  margin-top: 1.1rem;
  margin-bottom: 0.2rem;
  text-align: center;
  font-family: Syne, sans-serif;
  opacity: 0.7;
}
.auth-forgot-dark a {
  color: #fff;
  text-decoration: underline;
  cursor: pointer;
}
.auth-newsletter-dark {
  margin-top: 0.3rem;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  color: #fff;
  text-align: center;
  font-family: Syne, sans-serif;
}
.newsletter-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.9rem;
  opacity: 0.8;
}
.newsletter-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}
.checkmark {
  height: 1.2rem;
  width: 1.2rem;
  background-color: #222;
  border: 1px solid #fff;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.newsletter-checkbox input:checked ~ .checkmark {
  background-color: #888;
}
.checkmark:after {
  content: "";
  display: none;
  width: 0.5rem;
  height: 0.9rem;
  border: solid white;
  border-width: 0 0.2rem 0.2rem 0;
  transform: rotate(45deg);
}
.newsletter-checkbox input:checked ~ .checkmark:after {
  display: block;
}
.terms-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 30000;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}
.terms-modal-dark {
  background: #111;
  color: #fff;
  border-radius: 8px;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  max-width: 340px;
  width: 95vw;
  position: relative;
  font-family: Syne, sans-serif;
  box-shadow: 0 2px 24px rgba(0,0,0,0.18);
}
.terms-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
}
.terms-content {
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
  color: #fff;
}
.terms-content-small {
  font-size: 0.8rem;
}
.terms-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: 2px solid #fff;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  font-size: 2rem;
  color: #fff;
  cursor: pointer;
  z-index: 2;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 600px) {
  .auth-modal-dark {
    width: 100vw;
    min-width: 80vw;
    max-width: 80vw;
    border-radius: 0;
    box-shadow: none;
    box-sizing: border-box;
  }
  .auth-header {
    font-size: 1.05rem;
    margin-bottom: 0.8rem;
  }
  .terms-modal-dark {
    max-width: 85vw;
    padding: 1.2rem 0.5rem 1rem 0.5rem;
    width: 90vw;
    max-width: 320px;
  }
  .auth-form-dark input,
  .main-btn-dark,
  .google-btn-dark {
    width: 100vw !important;
    min-width: 70vw !important;
    max-width: 70vw !important;
    box-sizing: border-box;
  }
  .auth-modal-overlay {
    overflow-x: hidden;
  }
  
  /* Fix forgot password modal styling */
  .terms-modal-dark form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
  .terms-modal-dark form input {
    width: 80% !important;
    max-width: 250px;
    margin-bottom: 1rem;
    padding: 0.7rem;
    border-radius: 0;
    border: none;
    text-align: center;
    font-size: 1rem;
    font-family: Syne, sans-serif;
  }
  .terms-modal-dark form button {
    width: 80% !important;
    max-width: 250px;
    margin-bottom: 0.5rem;
    padding: 0.8rem 0;
    border-radius: 0;
    border: none;
    background: #888;
    color: #fff;
    font-size: 1.1rem;
    font-family: Syne, sans-serif;
    font-weight: 600;
    cursor: pointer;
    text-transform: lowercase;
  }
  
  /* Override the main button styles for reset password form */
  .terms-modal-dark form .main-btn-dark {
    width: 80% !important;
    max-width: 250px !important;
    min-width: auto !important;
  }
}

/* Modal dissolve animations */
.auth-fade-enter-active,
.auth-fade-leave-active {
  transition: opacity 1.5s ease;
}

.auth-fade-enter,
.auth-fade-leave-to {
  opacity: 0;
}

.auth-fade-enter-to,
.auth-fade-leave {
  opacity: 1;
}
</style> 